<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import PurchaseProjectItems from './PurchaseProjectItems.vue';
import ProductSearchDrawer from './ProductSearchDrawer.vue';
import { usePurchaseProjectsState } from './usePurchaseProjectsState.js';
import CloseCircleBeneficiaryPicker from 'dashboard/components-next/Contacts/CloseCircle/CloseCircleBeneficiaryPicker.vue';

const { contactId } = defineProps({
  contactId: {
    type: [String, Number],
    default: 'cus_001',
  },
  contactName: {
    type: String,
    default: '',
  },
  contactPhone: {
    type: String,
    default: '',
  },
  contactEmail: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['created']);

const { t } = useI18n();
const isOpen = ref(false);
const isSaving = ref(false);
const productSearchRef = ref(null);

const store = useStore();
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');
const { createPurchaseProject } = usePurchaseProjectsState();

const toSellerOption = user => ({
  id: user.id,
  name: user.name || user.email,
});

const sellers = computed(() => {
  const options = agents.value.filter(user => user.id).map(toSellerOption);
  const user = currentUser.value;

  if (user?.id && !options.some(option => option.id === user.id)) {
    return [toSellerOption(user), ...options];
  }

  return options;
});

const defaultSellerId = computed(() => {
  const user = currentUser.value;
  if (!user?.id) return sellers.value[0]?.id ?? '';
  return (
    sellers.value.find(seller => String(seller.id) === String(user.id))?.id ??
    sellers.value[0]?.id ??
    ''
  );
});

const ensureAgentsLoaded = () => {
  if (!agents.value.length) store.dispatch('agents/get');
};

const OCCASIONS = [
  { value: 'birthday', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.BIRTHDAY' },
  { value: 'wedding', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.WEDDING' },
  { value: 'gift', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.GIFT' },
  { value: 'personal', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.PERSONAL' },
  { value: 'other', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.OTHER' },
];

const form = reactive({
  title: '',
  sellerId: '',
  occasion: '',
  dueDate: '',
  internalNote: '',
  items: [],
  beneficiaryType: 'main_contact',
  beneficiaryId: null,
  beneficiarySnapshot: null,
});

const isFormValid = computed(() => form.title.trim().length > 0);

const existingProductIds = computed(() => form.items.map(i => i.productId));

const beneficiarySelection = computed({
  get: () => ({
    beneficiaryType: form.beneficiaryType,
    beneficiaryId: form.beneficiaryId,
    beneficiarySnapshot: form.beneficiarySnapshot,
  }),
  set: value => {
    form.beneficiaryType = value.beneficiaryType;
    form.beneficiaryId = value.beneficiaryId;
    form.beneficiarySnapshot = value.beneficiarySnapshot;
  },
});

const updateItem = updatedItem => {
  const idx = form.items.findIndex(
    i =>
      i.productId === updatedItem.productId &&
      i.variantId === updatedItem.variantId
  );
  if (idx !== -1) form.items[idx] = updatedItem;
};

const removeItem = removedItem => {
  form.items = form.items.filter(
    i =>
      !(
        i.productId === removedItem.productId &&
        i.variantId === removedItem.variantId
      )
  );
};

const addProduct = newItem => {
  form.items.push({ ...newItem, sortOrder: form.items.length });
};

const open = (initial = {}) => {
  ensureAgentsLoaded();
  form.title = '';
  form.sellerId = defaultSellerId.value;
  form.occasion = '';
  form.dueDate = '';
  form.internalNote = '';
  form.items = [];
  form.beneficiaryType = initial.beneficiaryType ?? 'main_contact';
  form.beneficiaryId = initial.beneficiaryId ?? null;
  form.beneficiarySnapshot = initial.beneficiarySnapshot ?? null;
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

const handleSubmit = async () => {
  if (!isFormValid.value || isSaving.value) return;
  const seller = sellers.value.find(
    s => String(s.id) === String(form.sellerId)
  );

  isSaving.value = true;
  try {
    const newProject = await createPurchaseProject(contactId, {
      title: form.title.trim(),
      sellerId: form.sellerId,
      sellerName: seller?.name ?? '',
      status: 'draft',
      occasion: form.occasion,
      eventDate: form.dueDate,
      internalNote: form.internalNote,
      beneficiaryType: form.beneficiaryType,
      beneficiaryId: form.beneficiaryId,
      beneficiarySnapshot: form.beneficiarySnapshot,
      metadata: {
        activity: [
          {
            type: 'created',
            label: t('PURCHASE_PROJECTS.ACTIVITY.CREATED'),
            date: Math.floor(Date.now() / 1000),
          },
        ],
      },
    });
    emit('created', newProject);
    close();
  } finally {
    isSaving.value = false;
  }
};

defineExpose({ open, close });

onMounted(ensureAgentsLoaded);
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[70] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="w-full sm:max-w-xl h-[92vh] max-h-[92vh] bg-n-solid-2 rounded-t-[1.5rem] sm:rounded-t-2xl shadow-xl flex flex-col overflow-hidden transition-transform duration-200 ease-in-out"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <!-- Header -->
        <div class="flex items-center gap-3 px-4 py-3 shrink-0 bg-n-solid-1">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            @click="close"
          />
          <h3 class="text-base font-medium text-n-slate-12 truncate">
            {{ t('PURCHASE_PROJECTS.FORM.TITLE') }}
          </h3>
        </div>

        <!-- Scrollable content -->
        <div class="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          <div class="bg-n-alpha-1 flex flex-col gap-2 py-2">
            <!-- Bloc Client -->
            <div class="bg-n-solid-1">
              <div class="px-4 py-3">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ t('PURCHASE_PROJECTS.DETAIL.CLIENT_SUMMARY_TITLE') }}
                </span>
              </div>
              <div class="px-4 pb-3 flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">{{
                  contactName
                }}</span>
                <span
                  v-if="contactPhone"
                  class="flex items-center gap-1 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-phone size-3 shrink-0" />
                  {{ contactPhone }}
                </span>
                <span
                  v-if="contactEmail"
                  class="flex items-center gap-1 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-mail size-3 shrink-0" />
                  {{ contactEmail }}
                </span>
              </div>
            </div>

            <!-- Bloc Beneficiaire -->
            <div class="bg-n-solid-1 px-4 pt-3 pb-4">
              <span class="text-sm font-semibold text-n-slate-12 block mb-4">
                {{ t('CLOSE_CIRCLE.BENEFICIARY.TITLE') }}
              </span>
              <CloseCircleBeneficiaryPicker
                v-model="beneficiarySelection"
                :contact-id="contactId"
              />
            </div>

            <!-- Bloc Informations du projet -->
            <div class="bg-n-solid-1 px-4 pt-3 pb-4">
              <span class="text-sm font-semibold text-n-slate-12 block mb-4">
                {{ t('PURCHASE_PROJECTS.DETAIL.PROJECT_INFO_TITLE') }}
              </span>
              <div class="flex flex-col gap-3">
                <!-- Nom du projet -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('PURCHASE_PROJECTS.FORM.PROJECT_NAME_LABEL') }}
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="no-margin w-full m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="
                      t('PURCHASE_PROJECTS.FORM.PROJECT_NAME_PLACEHOLDER')
                    "
                    @keydown.enter="handleSubmit"
                  />
                </div>

                <!-- 2 colonnes : Vendeur + Occasion -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.SELLER_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.sellerId"
                        class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      >
                        <option
                          v-for="seller in sellers"
                          :key="seller.id"
                          :value="seller.id"
                        >
                          {{ seller.name }}
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      />
                    </div>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.OCCASION_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.occasion"
                        class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      >
                        <option value="">—</option>
                        <option
                          v-for="occ in OCCASIONS"
                          :key="occ.value"
                          :value="occ.value"
                        >
                          {{ t(occ.labelKey) }}
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      />
                    </div>
                  </div>
                </div>

                <!-- Échéance -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('PURCHASE_PROJECTS.FORM.DUE_DATE_LABEL') }}
                  </label>
                  <input
                    v-model="form.dueDate"
                    type="date"
                    class="no-margin w-full m-0 h-9 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none appearance-none"
                  />
                </div>

                <!-- Note interne -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('PURCHASE_PROJECTS.FORM.INTERNAL_NOTE_LABEL') }}
                  </label>
                  <textarea
                    v-model="form.internalNote"
                    rows="2"
                    class="w-full m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none resize-none"
                    :placeholder="
                      t('PURCHASE_PROJECTS.FORM.INTERNAL_NOTE_PLACEHOLDER')
                    "
                  />
                </div>
              </div>
            </div>

            <!-- Bloc Sélection produits -->
            <div class="bg-n-solid-1">
              <PurchaseProjectItems
                :items="form.items"
                @update-item="updateItem"
                @remove-item="removeItem"
                @add-product="productSearchRef?.open()"
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="px-4 pb-4 pt-3 bg-n-solid-1 border-t border-n-strong shrink-0"
        >
          <Button
            variant="solid"
            color="blue"
            size="md"
            class="w-full"
            :label="t('PURCHASE_PROJECTS.FORM.CREATE')"
            :disabled="!isFormValid || isSaving"
            @click="handleSubmit"
          />
        </div>
      </div>
    </div>
  </Teleport>

  <ProductSearchDrawer
    ref="productSearchRef"
    :existing-product-ids="existingProductIds"
    @add-product="addProduct"
  />
</template>
